import { Collapse, Divider, IconButton, makeStyles, Paper, Theme } from '@material-ui/core';
import { Styles } from '@material-ui/core/styles/withStyles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import classnames from 'classnames';
import React from 'react';
import { BorderColor } from '@material-ui/icons';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { colors, ColorTypeEnum } from 'theme';
import ActionButton from '../Buttons/ActionButton';

type CollapsableCardStyleProps = {
  color?: ColorTypeEnum;
  disableCollapse?: boolean;
  maxWidth?: string;
  disableBorder?: boolean;
};

const collapsableCardStyles: Styles<Theme, CollapsableCardStyleProps> = theme => ({
  collapsableCard: {
    margin: 'auto',
    marginBottom: '10px',
    maxWidth: ({ maxWidth }) => maxWidth ?? '',
  },
  cardWrapper: {
    width: '100%',
    height: '55px',
    borderLeft: ({ disableBorder, color }) =>
      `${disableBorder ? '0px' : '9px'} solid ${color ? theme.palette[color].main : theme.palette.primary.main}`,
    borderRadius: '0px',
    cursor: ({ disableCollapse }) => (disableCollapse ? 'auto' : 'pointer'),
  },
  card: {
    display: 'flex',
    height: '100%',
    paddingLeft: '34px',
  },
  cardContent: {
    width: '90%',
    display: 'flex',
    alignItems: 'center',

    '& > p': {
      fontWeight: '600',
    },
  },
  cardActions: {
    width: '75px',
    maxWidth: '75px',
    alignSelf: 'center',
    display: 'flex',
    '& > *:not(:last-child)': {
      marginRight: '3px',
    },
    '& > *:not(:first-child)': {
      marginLeft: '3px',
    },
    '& button': {
      padding: '0.5rem',
      minWidth: 'initial',
    },
    '& a': {
      minWidth: 'initial',
    },
  },
  collapsedContent: {
    width: '100%',
    minHeight: '5vh',
    backgroundColor: colors.greyLightest,
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),

    '&:disabled span svg': {
      color: '#D9D9D9',
    },
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  icon: {
    border: 'solid 1px',
    borderRadius: '50%',
    color: theme.palette.text.primary,
  },

  divider: {
    margin: '10px 8px 10px 11px',
    height: '60%',
  },

  editActionButton: {
    margin: '8px',
    minWidth: '24px',
    '& svg': {
      color: '#2F3741',
      '& path:last-child': {
        fillOpacity: 1,
      },
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:hover svg': {
      color: '#0F626F',
    },
    '&:disabled svg': {
      color: '#D9D9D9',
    },
  },

  moreActionButton: {
    width: '0px',
    margin: '8px',
    '& svg': {
      fontSize: '1.9rem',
      color: '#2F3741',
    },
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:hover svg': {
      color: '#0F626F',
    },
    '&:disabled svg': {
      color: '#D9D9D9',
    },
  },
});

const useStyles = makeStyles(collapsableCardStyles);

type CollapsableCardProps = CollapsableCardStyleProps & {
  collapsableContent?: React.ReactNode;
  disableCollapse?: boolean;
  maxWidth?: string;
  defaultExpanded?: boolean;
  withActionButtons?: boolean;
  customActionButtons?: React.ReactNode;
};

const CollapsableCardWithActions: React.FC<CollapsableCardProps> = ({
  collapsableContent,
  disableCollapse,
  children,
  color,
  maxWidth,
  defaultExpanded,
  disableBorder,
  withActionButtons,
  customActionButtons,
}) => {
  const classes = useStyles({ disableCollapse, color, maxWidth, disableBorder });
  const [expanded, setExpanded] = React.useState(defaultExpanded ?? false);

  const handleExpandClick = (event: any) => {
    let classNameElement = event.target.getAttribute('class');
    if (null === classNameElement) {
      classNameElement = event.target.parentNode.getAttribute('class');
    }

    if (/MuiButton|userAction/.test(classNameElement)) {
      return;
    }

    if (!disableCollapse) {
      setExpanded(!expanded);
    }
  };

  return (
    <div className={classes.collapsableCard}>
      <Paper className={classes.cardWrapper}>
        <div className={classes.card}>
          <div className={classes.cardContent} onClick={handleExpandClick}>
            {children}
          </div>
          <IconButton
            className={classnames(classes.expand, {
              [classes.expandOpen]: expanded,
            })}
            aria-expanded={expanded}
            aria-label="show more"
            onClick={handleExpandClick}
            disabled={disableCollapse}
          >
            <ExpandMoreIcon className={classes.icon} />
          </IconButton>
          {withActionButtons && !customActionButtons && (
            <>
              <Divider className={classes.divider} orientation={'vertical'} flexItem />
              <div className={classes.cardActions}>
                <ActionButton
                  icon={BorderColor}
                  data-qa-id={'edit-button'}
                  disabled={true}
                  className={classes.editActionButton}
                />
                <ActionButton
                  icon={MoreVertIcon}
                  data-qa-id="action-button"
                  disabled={true}
                  className={classes.moreActionButton}
                ></ActionButton>
              </div>
            </>
          )}
          {customActionButtons && customActionButtons}
        </div>
      </Paper>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div className={classes.collapsedContent}>{collapsableContent}</div>
      </Collapse>
    </div>
  );
};

CollapsableCardWithActions.defaultProps = {
  color: ColorTypeEnum.PRIMARY,
};

export default CollapsableCardWithActions;
